import { useOktaAuth } from '@okta/okta-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Heading, Input, Stack, Button, Alert, Center } from '@chakra-ui/react';
import PulseLoader from "react-spinners/PulseLoader";
import { useTranslation } from 'react-i18next';

const ManagerAddExternalEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation();

  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [validationData, setValidationData] = useState(location.state?.validationData);
  const [loading, setLoading] = useState(false);
  const [buttonClickable, setButtonClickable] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [isAlreadyInUse, setIsAlreadyInUse] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [values, setValues] = useState({ email: "" });
  const [isInternalEmail, setIsInternalEmail] = useState(false);

  const isEmail = (email) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
  const isInternal = email => {
    if (/^[A-Z0-9._%+-]+@converse.com$/i.test(email)) {
      return true;
    } else if (/^[A-Z0-9._%+-]+@nike.com$/i.test(email)) {
      return true;
    } else {
      return false;
    }
  }

  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      // When user isn't authenticated, forget any user info
      setUserInfo(null);
      setValidationData(null);
    } else {
      oktaAuth.getUser().then((info) => {
        setUserInfo(info);
      });
      console.log("loaded:");
      console.log(validationData);
    }
  }, [authState, oktaAuth]); // Update if authState changes

  // Function to set the External Email in Okta
  function setExternalEmail(values) {
    setLoading(true);
    fetch(validationData.next, {
      method: 'POST',
      body: JSON.stringify({
        target_user: validationData.target_user,
        sessionid: validationData.session_id,
        external_email: values.email
      }),
      headers: {
        Authorization: 'Bearer ' + authState.accessToken.accessToken
      }
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.statusCode && data.statusCode !== 200) {
          if (data.message === "This email is already in use by another user") {
            setLoading(false);
            setIsAlreadyInUse(true);
          } else {
            navigate('/Error', { state: { errorMessage: data.message } });
          }
        } else {
          setLoading(false);
          console.log("add_external_email response:");
          console.log(data);
          sendPasswordResetEmail(data);
        }
      }).catch(error => {
        console.log(error);
        navigate('/Error', { state: { errorMessage: error.message } });
      });
  };

  // Function to set the External Email in Okta
  function sendPasswordResetEmail(validationData) {
    setLoading(true);
    fetch(validationData.next, {
      method: 'POST',
      body: JSON.stringify({
        user: validationData.user,
        sessionid: validationData.session_id
      }),
      headers: {
        Authorization: 'Bearer ' + authState.accessToken.accessToken
      }
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.statusCode && data.statusCode !== 200) {
          navigate('/Error', { state: { errorMessage: data.message } });
        } else {
          setApiResponse(data);
          console.log("password_reset response:");
          console.log(data);
          setPasswordResetSent(true);
          setButtonClickable(false);
          setLoading(false);
        }
      }).catch(error => {
        console.log(error);
        navigate('/Error', { state: { errorMessage: error.message } });
      });
  };

  function checkEmail(value) {
    if (isEmail(value.email) && !isInternal(value.email)) {
      setButtonClickable(true);
    } else {
      setButtonClickable(false);
    }
  }


  if (!authState) {
    oktaAuth.signInWithRedirect({ originalUri: '/' });
    return (
      <div>Loading...</div>
    );
  }

  return (
    <Box id="home">
      {loading &&
        <Box className="sweet-loading">
          <Box className="centered">
            <PulseLoader
              loading={loading}
              color="#dadada"
              size={30}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </Box>
        </Box>
      }
      <Box>
        { authState.isAuthenticated && !userInfo
        && <Box>{t('Loading user information')}...</Box>}

        {authState.isAuthenticated && userInfo && !passwordResetSent && (
          <Box>
            <Heading as="h1" size="2xl" mb={2}>{t('Add External Email Address')}</Heading>
            <Box p={6} borderWidth="1px" borderRadius="lg" bg="white" shadow="sm">
              <Box mb={4}>
                {t("manager_password_reset_message_no_secondary_email", { target_user: validationData?.target_user })}
              </Box>
              {isInternalEmail && (
                <Alert status="error" mb={4}>
                  {t("@nike.com and @converse.com email addresses are not allowed.")}
                </Alert>
              )}
              {isAlreadyInUse && (
                <Alert status="error" mb={4}>
                  {t("This email address is already registered to another user.")}
                </Alert>
              )}
              <Center>
                <Stack direction="row" maxW="500px" w="100%">
                  <Input
                    type='email'
                    value={values.email}
                    autoFocus
                    onChange={e => {
                      setIsAlreadyInUse(false);
                      setValues({ email: e.target.value });
                      checkEmail({ email: e.target.value });
                      setIsInternalEmail(isInternal(e.target.value));
                    }}
                    placeholder='example@gmail.com'
                    size="lg"
                  />
                  <Button
                    bg="nike.black"
                    color="nike.white"
                    _hover={{ bg: 'nike.grey.800' }}
                    disabled={!buttonClickable}
                    onClick={() => setExternalEmail(values)}
                    size="lg"
                  >
                    Submit
                  </Button>
                </Stack>
              </Center>
            </Box>
          </Box>
        )}
        {authState.isAuthenticated && userInfo && passwordResetSent && (
          <Center>
            <Box>
              <Heading as="h1" size="2xl" mb={4}>{t('External Email Updated')}</Heading>
              <Box p={8} borderWidth="1px" borderRadius="lg" bg="white" shadow="sm">
                <Heading as="h3" size="md">
                  {t('email_sent', { email: values.email })}
                </Heading>
              </Box>
            </Box>
          </Center>
        )}
      </Box>
    </Box>
  );
};
export default ManagerAddExternalEmail;
